import { FailedJobAttempt } from './types'
import { useDialogKeyboardShortcuts } from '../../hooks/useDialogKeyboardShortcuts'

interface FailedJobsModalProps {
  failedJobsModal: { queryId: string; queryName: string } | null
  failedJobs: FailedJobAttempt[]
  loadingFailedJobs: boolean
  errorTypeFilter: string | null
  errorCategoryFilter: string | null
  setFailedJobsModal: React.Dispatch<React.SetStateAction<{ queryId: string; queryName: string } | null>>
  setFailedJobs: React.Dispatch<React.SetStateAction<FailedJobAttempt[]>>
  setErrorTypeFilter: React.Dispatch<React.SetStateAction<string | null>>
  setErrorCategoryFilter: React.Dispatch<React.SetStateAction<string | null>>
}

export function FailedJobsModal({
  failedJobsModal,
  failedJobs,
  loadingFailedJobs,
  errorTypeFilter,
  errorCategoryFilter,
  setFailedJobsModal,
  setFailedJobs,
  setErrorTypeFilter,
  setErrorCategoryFilter,
}: FailedJobsModalProps) {
  const closeModal = () => {
    setFailedJobsModal(null)
    setFailedJobs([])
    setErrorTypeFilter(null)
    setErrorCategoryFilter(null)
  }

  useDialogKeyboardShortcuts({
    isOpen: failedJobsModal !== null,
    onCancel: closeModal,
    onConfirm: closeModal,
  })

  if (!failedJobsModal) return null

  const getErrorTypeColor = (type: string | null) => {
    switch (type) {
      case 'SIDEBAR_NOT_APPEARED': return 'bg-yellow-100 text-yellow-800'
      case 'LINK_NOT_FOUND': return 'bg-orange-100 text-orange-800'
      case 'SAVE_FAILED': return 'bg-red-100 text-red-800'
      case 'DATA_MISMATCH': return 'bg-purple-100 text-purple-800'
      case 'CARD_NOT_FOUND': return 'bg-pink-100 text-pink-800'
      case 'EXTRACTION_FAILED': return 'bg-blue-100 text-blue-800'
      default: return 'bg-surface-muted text-text-primary'
    }
  }

  const getCategoryColor = (category: string | null) => {
    switch (category) {
      case 'EXTRACTION': return 'border-l-4 border-blue-500'
      case 'SAVING': return 'border-l-4 border-red-500'
      case 'DOM': return 'border-l-4 border-yellow-500'
      case 'VALIDATION': return 'border-l-4 border-purple-500'
      case 'NETWORK': return 'border-l-4 border-orange-500'
      default: return 'border-l-4 border-border-strong'
    }
  }

  const filteredFailedJobs = failedJobs.filter(f => 
    (!errorTypeFilter || f.error_type === errorTypeFilter) &&
    (!errorCategoryFilter || f.error_category === errorCategoryFilter)
  )

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface-default rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col">
        <div className="px-6 py-4 border-b border-border-default flex items-center justify-between">
          <h2 className="text-xl font-semibold text-text-primary">
            Failed Jobs: {failedJobsModal.queryName}
          </h2>
          <button
            onClick={closeModal}
            className="text-text-muted hover:text-text-secondary"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {loadingFailedJobs ? (
            <div className="text-center text-text-muted py-8">Loading failed jobs...</div>
          ) : failedJobs.length === 0 ? (
            <div className="text-center text-text-muted py-8">No failed jobs found.</div>
          ) : (
            <>
              {/* Filters */}
              <div className="mb-4 flex gap-4 items-end">
                <div className="flex-1">
                  <label htmlFor="error-type-filter" className="block text-xs font-medium text-text-secondary mb-1">Filter by Error Type</label>
                  <select
                    id="error-type-filter"
                    value={errorTypeFilter || ''}
                    onChange={(e) => setErrorTypeFilter(e.target.value || null)}
                    className="w-full px-3 py-2 border border-border-strong rounded-md text-sm"
                  >
                    <option value="">All Types</option>
                    {Array.from(new Set(failedJobs.map(f => f.error_type).filter(Boolean))).map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-shrink-0">
                  <label htmlFor="error-category-filter" className="block text-xs font-medium text-text-secondary mb-1">Filter by Category</label>
                  <select
                    id="error-category-filter"
                    value={errorCategoryFilter || ''}
                    onChange={(e) => setErrorCategoryFilter(e.target.value || null)}
                    className="px-3 py-2 border border-border-strong rounded-md text-sm"
                    style={{ width: 'auto' }}
                  >
                    <option value="">All Categories</option>
                    {Array.from(new Set(failedJobs.map(f => f.error_category).filter(Boolean))).map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                {(errorTypeFilter || errorCategoryFilter) && (
                  <button
                    onClick={() => {
                      setErrorTypeFilter(null)
                      setErrorCategoryFilter(null)
                    }}
                    className="px-3 py-2 text-sm text-text-secondary hover:text-text-primary whitespace-nowrap"
                  >
                    Clear Filters
                  </button>
                )}
              </div>

              {/* Error Summary */}
              <div className="mb-4 p-3 bg-surface-subtle rounded-lg">
                <div className="text-sm text-text-secondary">
                  Showing {filteredFailedJobs.length} of {failedJobs.length} failed jobs
                </div>
              </div>

              {/* Failed Jobs List */}
              <div className="space-y-4">
                {filteredFailedJobs.map((attempt) => (
                  <div key={attempt.id} className={`border border-border-default rounded-lg p-4 ${getCategoryColor(attempt.error_category)}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="text-sm font-medium text-text-primary">
                            {attempt.linkedin_job_url ? (
                              <a
                                href={attempt.linkedin_job_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                Job ID: {attempt.linkedin_job_id}
                              </a>
                            ) : (
                              <>Job ID: {attempt.linkedin_job_id}</>
                            )}
                          </div>
                          {attempt.error_type && (
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getErrorTypeColor(attempt.error_type)}`}>
                              {attempt.error_type}
                            </span>
                          )}
                          {attempt.error_category && (
                            <span className="px-2 py-1 text-xs font-medium rounded bg-surface-muted text-text-secondary">
                              {attempt.error_category}
                            </span>
                          )}
                        </div>
                        
                        {attempt.failure_reason && (
                          <div className="text-sm text-text-secondary mt-2 mb-2">
                            <span className="font-medium">Reason:</span> {attempt.failure_reason}
                          </div>
                        )}

                        {/* Error Details */}
                        {attempt.error_details && Object.keys(attempt.error_details).length > 0 && (
                          <details className="mt-3">
                            <summary className="text-sm font-medium text-text-secondary cursor-pointer hover:text-text-primary">
                              Error Details
                            </summary>
                            <div className="mt-2 p-3 bg-surface-subtle rounded text-xs">
                              {attempt.error_details.attempt_number && (
                                <div className="mb-1"><span className="font-medium">Attempt:</span> {attempt.error_details.attempt_number}</div>
                              )}
                              {attempt.error_details.error_message && (
                                <div className="mb-1"><span className="font-medium">Error:</span> {attempt.error_details.error_message}</div>
                              )}
                              {attempt.error_details.extracted_data && (
                                <div className="mb-1">
                                  <span className="font-medium">Extracted Data:</span>
                                  <pre className="mt-1 text-xs bg-surface-default p-2 rounded overflow-auto">
                                    {JSON.stringify(attempt.error_details.extracted_data, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {attempt.error_details.card_found !== undefined && (
                                <div className="mb-1"><span className="font-medium">Card Found:</span> {attempt.error_details.card_found ? 'Yes' : 'No'}</div>
                              )}
                              {attempt.error_details.link_found !== undefined && (
                                <div className="mb-1"><span className="font-medium">Link Found:</span> {attempt.error_details.link_found ? 'Yes' : 'No'}</div>
                              )}
                              {attempt.error_details.sidebar_appeared !== undefined && (
                                <div className="mb-1"><span className="font-medium">Sidebar Appeared:</span> {attempt.error_details.sidebar_appeared ? 'Yes' : 'No'}</div>
                              )}
                              {attempt.error_details.tab_hidden !== undefined && (
                                <div className="mb-1"><span className="font-medium">Tab Hidden:</span> {attempt.error_details.tab_hidden ? 'Yes' : 'No'}</div>
                              )}
                              {attempt.error_details.stack_trace && (
                                <div className="mb-1">
                                  <span className="font-medium">Stack Trace:</span>
                                  <pre className="mt-1 text-xs bg-surface-default p-2 rounded overflow-auto max-h-32">
                                    {attempt.error_details.stack_trace}
                                  </pre>
                                </div>
                              )}
                              {/* Show all other details */}
                              {Object.entries(attempt.error_details)
                                .filter(([key]) => !['attempt_number', 'error_message', 'extracted_data', 'card_found', 'link_found', 'sidebar_appeared', 'tab_hidden', 'stack_trace'].includes(key))
                                .map(([key, value]) => (
                                  <div key={key} className="mb-1">
                                    <span className="font-medium">{key}:</span> {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                  </div>
                                ))}
                            </div>
                          </details>
                        )}

                        <div className="text-xs text-text-muted mt-2">
                          Attempted: {new Date(attempt.attempted_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-border-default flex justify-end">
          <button
            onClick={closeModal}
            className="px-4 py-2 bg-surface-muted text-text-primary rounded-lg hover:bg-border-default"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
